import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { RegisteredService } from '../../../../api/gen/api/registered.service';
import { GuestService } from '../../../../api/gen/api/guest.service';
import { ParkerService } from '../../../../api/gen/api/parker.service';
import { LicensePlateDto } from '../../../../api/gen/model/licensePlateDto';
import { LicensePlateType } from '../../../../api/gen/model/licensePlateType';
import { ParkerType } from '../../../../api/gen/model/parkerType';
import {
    JjamesButtonComponent, JJamesAlertComponent,
    PlateComponent, PLATE_COUNTRIES, MAX_PLATE_LENGTH,
    extractRegion, normalizePlate, sanitizePlateInput
} from 'shared-lib';

export interface ManageMemberPlatesDialogData {
    /** Parker id — the same id the organization membership carries, for either parker type. */
    parkerId: string;
    /** Name or email, for the dialog subtitle only. */
    displayName: string;
    /**
     * Which endpoint family owns this parker. Required in practice: a member with no account is a
     * GuestParker, and every registered-parker endpoint answers 404 for one. Absent is read as
     * RegisteredParker, which is the historical behaviour of this dialog.
     */
    parkerType?: ParkerType;
}

export type DialogViewMode = 'list' | 'bulk-add' | 'single-add';

export interface ParsedBulkPlate {
    raw: string;
    normalized: string;
    isValid: boolean;
    isDuplicateInInput: boolean;
    isAlreadyAssigned: boolean;
}

export interface BulkOperationResult {
    plate: string;
    success: boolean;
    error?: string;
}

/**
 * Operator-side "manage this member's license plates".
 *
 * Supports single add, bulk paste/import, multi-select bulk removal,
 * and real-time search while strictly obeying the canonical plate normalization and backend policies.
 */
@Component({
    selector: 'app-manage-member-plates-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        JjamesButtonComponent,
        JJamesAlertComponent,
        PlateComponent
    ],
    templateUrl: './manage-member-plates-dialog.component.html'
})
export class ManageMemberPlatesDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<ManageMemberPlatesDialogComponent>);
    private readonly registeredService = inject(RegisteredService);
    private readonly guestService = inject(GuestService);
    private readonly parkerService = inject(ParkerService);
    private readonly fb = inject(FormBuilder);

    readonly data = inject<ManageMemberPlatesDialogData>(MAT_DIALOG_DATA);

    /**
     * An organization member may be a parker with no account at all. Guests and registered parkers
     * are separate aggregates behind separate controllers.
     */
    readonly isGuest = this.data.parkerType === ParkerType.GuestParker;

    readonly countries = PLATE_COUNTRIES;
    readonly maxPlateLength = MAX_PLATE_LENGTH;

    readonly plateTypes: { value: LicensePlateType; label: string }[] = [
        { value: 'Personal', label: 'Personal' },
        { value: 'Commercial', label: 'Commercial' },
        { value: 'Government', label: 'Government' },
        { value: 'Other', label: 'Other' }
    ];

    // Main view mode
    viewMode = signal<DialogViewMode>('list');

    // Data state
    plates = signal<LicensePlateDto[]>([]);
    loading = signal(false);
    submitting = signal(false);
    bulkProcessing = signal(false);
    bulkProgress = signal<{ current: number; total: number; item: string } | null>(null);

    // Messages
    errorMessage = signal<string | null>(null);
    successMessage = signal<string | null>(null);

    // Single item removal
    removingId = signal<string | null>(null);
    removeConfirmId = signal<string | null>(null);

    // Search & filter in list mode
    searchQuery = signal('');

    // Multi-select in list mode
    selectedPlateIds = signal<Set<string>>(new Set());
    bulkRemoveConfirm = signal(false);

    // Bulk Add state
    bulkInput = signal('');
    bulkCountry = signal('AT');
    bulkType = signal<LicensePlateType>('Personal');
    bulkAddResults = signal<BulkOperationResult[] | null>(null);

    // Copy indicator
    copiedId = signal<string | null>(null);

    /** True once anything was added or removed, so the caller knows to reload the organization. */
    private changed = false;

    // Single add live preview state
    plateValue = signal('');
    plateRegion = signal('');
    selectedCountryCode = signal('AT');

    readonly selectedCountry = computed(() =>
        this.countries.find(c => c.code === this.selectedCountryCode()) ?? this.countries[0]);

    addForm = this.fb.group({
        country: ['AT', Validators.required],
        plate: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(12),
        Validators.pattern(/^[A-Z0-9ÄÖÜ]+$/)]],
        plateRepeat: ['', Validators.required],
        type: ['Personal' as LicensePlateType, Validators.required]
    }, { validators: [platesMatchValidator] });

    // Computed filtered list
    readonly filteredPlates = computed(() => {
        const q = this.searchQuery().trim().toLowerCase();
        const all = this.plates();
        if (!q) return all;
        return all.filter(p =>
            (p.fullNumber ?? '').toLowerCase().includes(q) ||
            (p.countryCode ?? '').toLowerCase().includes(q)
        );
    });

    readonly isAllSelected = computed(() => {
        const filtered = this.filteredPlates();
        if (filtered.length === 0) return false;
        const set = this.selectedPlateIds();
        return filtered.every(p => p.id && set.has(p.id));
    });

    readonly selectedCount = computed(() => this.selectedPlateIds().size);

    // Bulk parse computed
    readonly parsedBulkPlates = computed<ParsedBulkPlate[]>(() => {
        const rawText = this.bulkInput();
        if (!rawText.trim()) return [];

        const tokens = rawText
            .split(/[\r\n,;\t]+/)
            .map(t => t.trim())
            .filter(t => t.length > 0);

        const existingMap = new Set(this.plates().map(p => normalizePlate(p.fullNumber)));
        const seenInInput = new Set<string>();

        return tokens.map(raw => {
            const normalized = normalizePlate(raw);
            const isValidLength = normalized.length >= 3 && normalized.length <= 32;
            const isValidChars = /^[\p{L}0-9]+$/u.test(normalized);
            const isValid = isValidLength && isValidChars;
            const isDuplicateInInput = seenInInput.has(normalized);
            if (isValid) {
                seenInInput.add(normalized);
            }
            const isAlreadyAssigned = existingMap.has(normalized);

            return {
                raw,
                normalized,
                isValid,
                isDuplicateInInput,
                isAlreadyAssigned
            };
        });
    });

    readonly validBulkPlatesToAdd = computed(() => {
        const parsed = this.parsedBulkPlates();
        const unique = new Map<string, ParsedBulkPlate>();
        for (const p of parsed) {
            if (p.isValid && !p.isAlreadyAssigned && !unique.has(p.normalized)) {
                unique.set(p.normalized, p);
            }
        }
        return Array.from(unique.values());
    });

    readonly duplicateInInputCount = computed(() => {
        return this.parsedBulkPlates().filter(p => p.isDuplicateInInput).length;
    });

    readonly alreadyAssignedCount = computed(() => {
        return this.parsedBulkPlates().filter(p => p.isAlreadyAssigned && !p.isDuplicateInInput).length;
    });

    readonly invalidBulkCount = computed(() => {
        return this.parsedBulkPlates().filter(p => !p.isValid).length;
    });

    constructor() {
        this.addForm.get('plate')!.valueChanges.subscribe(value => {
            this.plateValue.set(normalizePlate(value));
            this.plateRegion.set(extractRegion(
                value ?? '', this.selectedCountry().regionLength, this.selectedCountry().regions));
        });
        this.addForm.get('country')!.valueChanges.subscribe(value => {
            this.selectedCountryCode.set(value ?? 'AT');
        });
        this.load();
    }

    load(): void {
        this.loading.set(true);
        const request$ = this.isGuest
            ? this.guestService.operatorGuestParkerGuestParkerIdGet(this.data.parkerId)
            : this.registeredService.operatorRegisteredParkerRegisteredParkerIdGet(this.data.parkerId, false);

        request$.subscribe({
            next: (parker) => {
                this.plates.set(parker?.activeLicenses ?? []);
                this.loading.set(false);
            },
            error: (err: unknown) => {
                this.errorMessage.set(this.extractReason(err) ?? 'Could not load the license plates for this member.');
                this.loading.set(false);
            }
        });
    }

    setViewMode(mode: DialogViewMode): void {
        this.viewMode.set(mode);
        this.errorMessage.set(null);
        this.successMessage.set(null);
        this.removeConfirmId.set(null);
        this.bulkRemoveConfirm.set(false);
    }

    // ── Multi-select & Bulk Delete ─────────────────────────────────────────────

    toggleSelectPlate(plateId: string): void {
        const set = new Set(this.selectedPlateIds());
        if (set.has(plateId)) {
            set.delete(plateId);
        } else {
            set.add(plateId);
        }
        this.selectedPlateIds.set(set);
    }

    toggleSelectAll(): void {
        const filtered = this.filteredPlates();
        const set = new Set(this.selectedPlateIds());
        const allFilteredSelected = filtered.length > 0 && filtered.every(p => p.id && set.has(p.id));
        if (allFilteredSelected) {
            filtered.forEach(p => { if (p.id) set.delete(p.id); });
        } else {
            filtered.forEach(p => { if (p.id) set.add(p.id); });
        }
        this.selectedPlateIds.set(set);
    }

    clearSelection(): void {
        this.selectedPlateIds.set(new Set());
        this.bulkRemoveConfirm.set(false);
    }

    async executeBulkRemove(): Promise<void> {
        const selectedIds = Array.from(this.selectedPlateIds());
        if (!selectedIds.length || this.bulkProcessing()) return;

        this.bulkProcessing.set(true);
        this.bulkRemoveConfirm.set(false);
        this.errorMessage.set(null);
        this.successMessage.set(null);

        const platesToRemove = this.plates().filter(p => p.id && selectedIds.includes(p.id));
        let succeededCount = 0;
        const failedErrors: string[] = [];

        for (let i = 0; i < platesToRemove.length; i++) {
            const p = platesToRemove[i];
            this.bulkProgress.set({
                current: i + 1,
                total: platesToRemove.length,
                item: p.fullNumber ?? p.id!
            });

            try {
                if (this.isGuest) {
                    await firstValueFrom(this.guestService.operatorGuestParkerGuestParkerIdRemoveLicensePlateLicensePlateIdPost(
                        this.data.parkerId, p.id!
                    ));
                } else {
                    await firstValueFrom(this.registeredService.operatorRegisteredParkerRegisteredParkerIdRemoveLicensePlateLicensePlateIdPost(
                        this.data.parkerId, p.id!
                    ));
                }
                succeededCount++;
                this.changed = true;
            } catch (err: unknown) {
                failedErrors.push(`${p.fullNumber}: ${this.extractReason(err) ?? 'Failed to remove'}`);
            }
        }

        this.bulkProcessing.set(false);
        this.bulkProgress.set(null);
        this.selectedPlateIds.set(new Set());

        if (failedErrors.length === 0) {
            this.successMessage.set(`Successfully removed ${succeededCount} license plate(s).`);
        } else if (succeededCount > 0) {
            this.successMessage.set(`Removed ${succeededCount} plate(s).`);
            this.errorMessage.set(`Failed to remove ${failedErrors.length} plate(s): ${failedErrors.join(', ')}`);
        } else {
            this.errorMessage.set(`Failed to remove plates: ${failedErrors.join(', ')}`);
        }

        this.load();
    }

    // ── Bulk Add / Quick Import ────────────────────────────────────────────────

    removeParsedChip(normalizedPlate: string): void {
        // Remove the plate from the raw bulkInput
        const tokens = this.bulkInput()
            .split(/[\r\n,;\t]+/)
            .map(t => t.trim())
            .filter(t => normalizePlate(t) !== normalizedPlate);
        this.bulkInput.set(tokens.join('\n'));
    }

    async executeBulkAdd(): Promise<void> {
        const platesToAdd = this.validBulkPlatesToAdd();
        if (!platesToAdd.length || this.bulkProcessing()) return;

        this.bulkProcessing.set(true);
        this.errorMessage.set(null);
        this.successMessage.set(null);
        this.bulkAddResults.set(null);

        const country = this.bulkCountry();
        const type = this.bulkType();
        const results: BulkOperationResult[] = [];

        for (let i = 0; i < platesToAdd.length; i++) {
            const item = platesToAdd[i];
            this.bulkProgress.set({
                current: i + 1,
                total: platesToAdd.length,
                item: item.normalized
            });

            try {
                const regResult = await firstValueFrom(this.parkerService.parkerRegisterLicensePlatePost({
                    fullNumber: item.normalized,
                    countryCode: country,
                    type: type
                }));

                if (!regResult?.id) {
                    results.push({ plate: item.normalized, success: false, error: 'Registration returned no ID.' });
                    continue;
                }

                if (this.isGuest) {
                    await firstValueFrom(this.guestService.operatorGuestParkerGuestParkerIdAssignLicensePlatesPost(
                        this.data.parkerId, { licensePlateIDs: [regResult.id] }
                    ));
                } else {
                    await firstValueFrom(this.registeredService.operatorRegisteredParkerRegisteredParkerIdAssignLicensePlateLicensePlateIdPost(
                        this.data.parkerId, regResult.id
                    ));
                }

                results.push({ plate: item.normalized, success: true });
                this.changed = true;
            } catch (err: unknown) {
                results.push({
                    plate: item.normalized,
                    success: false,
                    error: this.extractReason(err) ?? 'Assignment failed'
                });
            }
        }

        this.bulkProcessing.set(false);
        this.bulkProgress.set(null);
        this.bulkAddResults.set(results);

        const succeeded = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        if (failed === 0) {
            this.successMessage.set(`Successfully added all ${succeeded} license plate(s).`);
            this.bulkInput.set('');
        } else if (succeeded > 0) {
            this.successMessage.set(`Added ${succeeded} plate(s). ${failed} plate(s) failed.`);
        } else {
            this.errorMessage.set(`Failed to add ${failed} plate(s). See details below.`);
        }

        this.load();
    }

    // ── Single Add Form ───────────────────────────────────────────────────────

    onPlateFieldInput(event: Event, control: 'plate' | 'plateRepeat'): void {
        const input = event.target as HTMLInputElement;
        const raw = input.value;
        const cleaned = sanitizePlateInput(raw);
        if (cleaned === raw) return;

        const caret = input.selectionStart ?? raw.length;
        const rejectedBeforeCaret = caret - sanitizePlateInput(raw.slice(0, caret)).length;
        this.addForm.get(control)!.setValue(cleaned);
        input.value = cleaned;
        const restored = Math.max(0, caret - rejectedBeforeCaret);
        input.setSelectionRange(restored, restored);
    }

    get platesMismatch(): boolean {
        const repeat = this.addForm.get('plateRepeat');
        return !!repeat?.touched && !!repeat.value && this.addForm.hasError('platesMismatch');
    }

    cancelAdd(): void {
        this.viewMode.set('list');
        this.addForm.reset({ country: 'AT', plate: '', plateRepeat: '', type: 'Personal' });
        this.plateValue.set('');
        this.plateRegion.set('');
        this.errorMessage.set(null);
    }

    addPlate(): void {
        if (this.submitting()) return;
        if (this.addForm.invalid) {
            this.addForm.markAllAsTouched();
            return;
        }
        this.submitting.set(true);
        this.errorMessage.set(null);
        this.successMessage.set(null);

        const raw = this.addForm.getRawValue();
        const fullNumber = normalizePlate(raw.plate);

        this.parkerService.parkerRegisterLicensePlatePost({
            fullNumber: fullNumber,
            countryCode: raw.country ?? undefined,
            type: raw.type ?? 'Personal'
        }).subscribe({
            next: (plate) => {
                if (!plate?.id) {
                    this.submitting.set(false);
                    this.errorMessage.set('The plate was registered but came back without an id.');
                    return;
                }
                this.assignPlate(plate.id, fullNumber);
            },
            error: (err: unknown) => {
                this.submitting.set(false);
                this.errorMessage.set(this.extractReason(err) ?? 'Could not register the license plate.');
            }
        });
    }

    private assignPlate(licensePlateId: string, fullNumber: string): void {
        const request$ = this.isGuest
            ? this.guestService.operatorGuestParkerGuestParkerIdAssignLicensePlatesPost(
                this.data.parkerId, { licensePlateIDs: [licensePlateId] })
            : this.registeredService.operatorRegisteredParkerRegisteredParkerIdAssignLicensePlateLicensePlateIdPost(
                this.data.parkerId, licensePlateId);

        request$.subscribe({
            next: () => {
                this.submitting.set(false);
                this.changed = true;
                this.successMessage.set(`${fullNumber} added to ${this.data.displayName}.`);
                this.cancelAdd();
                this.load();
            },
            error: (err: unknown) => {
                this.submitting.set(false);
                this.errorMessage.set(this.extractReason(err)
                    ?? `${fullNumber} could not be assigned to this member.`);
            }
        });
    }

    // ── Single Removal ────────────────────────────────────────────────────────

    confirmRemove(plateId: string): void {
        this.removeConfirmId.set(plateId);
    }

    cancelRemove(): void {
        this.removeConfirmId.set(null);
    }

    removePlate(plate: LicensePlateDto): void {
        const id = plate.id;
        if (!id || this.removingId()) return;
        this.removingId.set(id);
        this.errorMessage.set(null);
        this.successMessage.set(null);

        const request$ = this.isGuest
            ? this.guestService.operatorGuestParkerGuestParkerIdRemoveLicensePlateLicensePlateIdPost(
                this.data.parkerId, id)
            : this.registeredService.operatorRegisteredParkerRegisteredParkerIdRemoveLicensePlateLicensePlateIdPost(
                this.data.parkerId, id);

        request$.subscribe({
            next: () => {
                this.removingId.set(null);
                this.removeConfirmId.set(null);
                this.changed = true;
                this.successMessage.set(`${plate.fullNumber} removed.`);
                this.load();
            },
            error: (err: unknown) => {
                this.removingId.set(null);
                this.removeConfirmId.set(null);
                this.errorMessage.set(this.extractReason(err) ?? 'Could not remove this license plate.');
            }
        });
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    copyToClipboard(text: string, id?: string): void {
        navigator.clipboard.writeText(text).then(() => {
            const key = id ?? text;
            this.copiedId.set(key);
            setTimeout(() => {
                if (this.copiedId() === key) {
                    this.copiedId.set(null);
                }
            }, 1800);
        });
    }

    close(): void {
        this.dialogRef.close(this.changed);
    }

    private extractReason(err: unknown): string | null {
        const body = (err as { error?: unknown })?.error;

        if (typeof body === 'string' && body.trim().length > 0) {
            return body.trim();
        }

        if (body && typeof body === 'object') {
            const { detail, title } = body as { detail?: unknown; title?: unknown };
            const text = [detail, title].find(v => typeof v === 'string' && v.trim().length > 0);
            if (typeof text === 'string') return text.trim();
        }

        return null;
    }
}

function platesMatchValidator(group: AbstractControl): ValidationErrors | null {
    const plate = normalizePlate(group.get('plate')?.value);
    const repeat = normalizePlate(group.get('plateRepeat')?.value);
    return plate && repeat && plate !== repeat ? { platesMismatch: true } : null;
}
